import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../../components/AppShell';
import AdminTabs from '../../components/AdminTabs';
import FormShell from '../../components/FormShell';
import ErrorBanner from '../../components/ErrorBanner';
import RichTextEditor from '../../components/RichTextEditor';
import PhotoUploader from '../../components/PhotoUploader';
import { supabase } from '../../lib/supabase';
import { sanitizeRichText } from '@viet-parenting/supabase/sanitize';
import { slugifyWithTimestamp } from '@viet-parenting/supabase/slug';
import type {
  Resource,
  ResourceType,
  ResourceAgeGroup,
  ResourceContent,
  ResourceAccess,
  Accent,
  Status,
  Country,
} from '@viet-parenting/supabase/types';
import { ResourceDetail } from '@viet-parenting/content/detail';
import PreviewModal from '../../components/PreviewModal';
import PreviewButton from '../../components/PreviewButton';
import { draftIdentity } from '../../lib/preview';

const TYPES: ReadonlyArray<{ value: ResourceType; label: string }> = [
  { value: 'video', label: 'Video' },
  { value: 'audio', label: 'Âm thanh (Audio)' },
  { value: 'book', label: 'Sách & Ebook' },
  { value: 'worksheet', label: 'Bài tập & In ấn' },
  { value: 'app', label: 'Ứng dụng (App)' },
  { value: 'other', label: 'Khác' },
];

const AGES: ReadonlyArray<{ value: ResourceAgeGroup; label: string }> = [
  { value: '0-5', label: '0–5 (Mầm non)' },
  { value: '6-11', label: '6–11 (Tiểu học)' },
  { value: '12-15', label: '12–15 (THCS)' },
  { value: '15+', label: 'Trên 15 tuổi' },
  { value: 'parents', label: 'Phụ huynh & Nhà giáo' },
];

const CONTENTS: ReadonlyArray<{ value: ResourceContent; label: string }> = [
  { value: 'language', label: 'Ngôn ngữ' },
  { value: 'culture', label: 'Văn hoá & Di sản' },
  { value: 'stories', label: 'Câu chuyện & Cổ tích' },
  { value: 'identity', label: 'Tư duy & Bản sắc' },
];

const ACCESSES: ReadonlyArray<{ value: ResourceAccess; label: string }> = [
  { value: 'free', label: 'Miễn phí' },
  { value: 'freemium', label: 'Freemium' },
  { value: 'paid', label: 'Có phí' },
];

const ACCENTS: ReadonlyArray<{ value: Accent; label: string }> = [
  { value: 'northern', label: 'Giọng Bắc' },
  { value: 'central', label: 'Giọng Trung' },
  { value: 'southern', label: 'Giọng Nam' },
  { value: 'mixed', label: 'Nhiều giọng' },
];

interface FormState {
  name_vi: string;
  name_en: string;
  description_vi: string;
  description_en: string;
  photos: string[];
  resource_url: string;
  types: ResourceType[];
  age_groups: ResourceAgeGroup[];
  contents: ResourceContent[];
  accesses: ResourceAccess[];
  accents: Accent[];
  country: string;
  status: Status;
  featured: boolean;
}

const EMPTY: FormState = {
  name_vi: '',
  name_en: '',
  description_vi: '',
  description_en: '',
  photos: [],
  resource_url: '',
  types: [],
  age_groups: [],
  contents: [],
  accesses: [],
  accents: [],
  country: 'global',
  status: 'pending',
  featured: false,
};

/**
 * The database shape of the form, shared by the save mutation and the preview
 * so an admin can never see something a save would not write.
 */
function toPayload(state: FormState) {
  return {
    name_vi: state.name_vi.trim(),
    name_en: state.name_en.trim() || null,
    description_vi: state.description_vi ? sanitizeRichText(state.description_vi) : null,
    description_en: state.description_en ? sanitizeRichText(state.description_en) : null,
    photos: state.photos,
    resource_url: state.resource_url.trim() || null,
    types: state.types,
    age_groups: state.age_groups,
    contents: state.contents,
    accesses: state.accesses,
    accents: state.accents,
    country: state.country.trim() || 'global',
    status: state.status,
    featured: state.featured,
  };
}

function toPreviewRow(state: FormState, id: string | undefined): Resource {
  return {
    ...draftIdentity(state.name_vi),
    ...(id ? { id } : {}),
    ...toPayload(state),
  } as unknown as Resource;
}

interface Props {
  mode: 'new' | 'edit';
}

export default function AdminResourceForm({ mode }: Props) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<FormState>(EMPTY);
  const [previewing, setPreviewing] = useState(false);
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data } = await supabase.from('countries').select('*').order('sort');
      if (!active) return;
      setCountries((data ?? []) as Country[]);
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (mode === 'new' || !id) return;
    let active = true;
    (async () => {
      const { data, error: dbError } = await supabase
        .from('resources')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (!active) return;
      if (dbError || !data) {
        setError(dbError?.message ?? 'Không tìm thấy tài nguyên.');
        setLoading(false);
        return;
      }
      const r = data as Resource;
      setState({
        name_vi: r.name_vi,
        name_en: r.name_en ?? '',
        description_vi: r.description_vi ?? '',
        description_en: r.description_en ?? '',
        photos: r.photos,
        resource_url: r.resource_url ?? '',
        types: r.types,
        age_groups: r.age_groups,
        contents: r.contents,
        accesses: r.accesses,
        accents: r.accents,
        country: r.country,
        status: r.status,
        featured: r.featured ?? false,
      });
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [mode, id]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setState((s) => ({ ...s, [key]: value }));

  const toggle = <T extends string>(key: keyof FormState, value: T) => {
    const list = state[key] as T[];
    update(key, (list.includes(value) ? list.filter((x) => x !== value) : [...list, value]) as FormState[typeof key]);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!state.name_vi.trim()) {
      setError('Tên tài nguyên là bắt buộc.');
      return;
    }
    setSaving(true);
    setError(null);

    const payload = toPayload(state);

    const result =
      mode === 'new'
        ? await supabase
            .from('resources')
            .insert({ ...payload, slug: slugifyWithTimestamp(state.name_vi) })
        : await supabase.from('resources').update(payload).eq('id', id!);

    setSaving(false);
    if (result.error) {
      setError(result.error.message);
      return;
    }
    navigate('/admin/resources');
  };

  const handleDelete = async () => {
    if (mode !== 'edit' || !id) return;
    const ok = window.confirm(
      `Xóa tài nguyên "${state.name_vi}"? Hành động này không thể hoàn tác.`,
    );
    if (!ok) return;
    setDeleting(true);
    setError(null);
    const { error: dbError } = await supabase.from('resources').delete().eq('id', id);
    setDeleting(false);
    if (dbError) {
      setError(dbError.message);
      return;
    }
    navigate('/admin/resources');
  };

  const title = mode === 'new' ? 'Thêm tài nguyên mới' : 'Chỉnh sửa tài nguyên';

  if (loading) {
    return (
      <AppShell>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <AdminTabs active="resources" />
          <FormShell title={title} maxWidth="max-w-6xl">
            <p className="text-sm text-gray-400">Đang tải…</p>
          </FormShell>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <AdminTabs active="resources" />
        <FormShell title={title} maxWidth="max-w-6xl">
          <ErrorBanner message={error} />

          <form onSubmit={handleSubmit}>
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="space-y-8">
            <Section title="Thông tin cơ bản">
              <Field label="Đường dẫn tài nguyên (URL)">
                <input
                  type="url"
                  value={state.resource_url}
                  onChange={(e) => update('resource_url', e.target.value)}
                  placeholder="https://example.com/resource"
                  className={inputClass}
                />
              </Field>
              <Field label="Quốc gia" required>
                <select
                  required
                  value={state.country}
                  onChange={(e) => update('country', e.target.value)}
                  className={inputClass}
                >
                  <option value="global">🌐 Toàn cầu (Global)</option>
                  {countries.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.flag} {c.name_vi} ({c.code})
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Tên" required>
                <input
                  type="text"
                  required
                  value={state.name_vi}
                  onChange={(e) => update('name_vi', e.target.value)}
                  placeholder="Sách Bé Học Tiếng Việt"
                  className={inputClass}
                />
              </Field>
              <Field label="Tên tiếng Anh">
                <input
                  type="text"
                  value={state.name_en}
                  onChange={(e) => update('name_en', e.target.value)}
                  placeholder="Vietnamese Learning Book for Kids"
                  className={inputClass}
                />
              </Field>
              <Field label="Mô tả (VI)">
                <RichTextEditor
                  value={state.description_vi}
                  onChange={(html) => update('description_vi', html)}
                  placeholder="Mô tả tài nguyên..."
                />
              </Field>
              <Field label="Mô tả (EN)">
                <RichTextEditor
                  value={state.description_en}
                  onChange={(html) => update('description_en', html)}
                  placeholder="Description..."
                />
              </Field>
            </Section>

            <Section
              title="Hình ảnh"
              subtitle="Kéo thả để sắp xếp. Ảnh đầu tiên là ảnh đại diện."
            >
              <PhotoUploader
                value={state.photos}
                onChange={(ids) => update('photos', ids)}
                maxFiles={4}
              />
            </Section>
              </div>

              <div className="space-y-8">
            <Section title="Phân loại">
              <CheckboxGroup
                label="Loại tài nguyên"
                options={TYPES}
                values={state.types}
                onToggle={(v) => toggle('types', v)}
              />
              <CheckboxGroup
                label="Độ tuổi"
                options={AGES}
                values={state.age_groups}
                onToggle={(v) => toggle('age_groups', v)}
              />
              <CheckboxGroup
                label="Nội dung"
                options={CONTENTS}
                values={state.contents}
                onToggle={(v) => toggle('contents', v)}
              />
              <CheckboxGroup
                label="Truy cập"
                options={ACCESSES}
                values={state.accesses}
                onToggle={(v) => toggle('accesses', v)}
              />
              <CheckboxGroup
                label="Phương ngữ"
                options={ACCENTS}
                values={state.accents}
                onToggle={(v) => toggle('accents', v)}
              />
            </Section>
              </div>
            </div>

            <div className="sticky bottom-0 z-10 -mx-4 sm:-mx-6 mt-8 px-4 sm:px-6 py-4 bg-white/75 backdrop-blur-md border-t border-gray-200 flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => navigate('/admin/resources')}
                className="border border-gray-200 text-gray-700 text-sm font-medium px-5 py-2.5 rounded-lg hover:border-red-300 transition-colors"
              >
                Hủy
              </button>
              <PreviewButton onClick={() => setPreviewing(true)} disabled={!state.name_vi.trim()} />
              {mode === 'edit' && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting || saving}
                  className="border border-red-200 text-red-700 text-sm font-medium px-5 py-2.5 rounded-lg hover:bg-red-50 hover:border-red-300 disabled:opacity-50 transition-colors"
                >
                  {deleting ? 'Đang xóa…' : 'Xóa'}
                </button>
              )}
              <div className="flex items-center gap-4 ml-auto text-sm flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={state.featured}
                    onChange={(e) => update('featured', e.target.checked)}
                  />
                  <span className="font-medium text-gray-700">★ Featured</span>
                </label>
                <span className="font-medium text-gray-700">Trạng thái:</span>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="status"
                    value="pending"
                    checked={state.status === 'pending'}
                    onChange={() => update('status', 'pending')}
                  />
                  Pending
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="status"
                    value="verified"
                    checked={state.status === 'verified'}
                    onChange={() => update('status', 'verified')}
                  />
                  Active
                </label>
              </div>
              <button
                type="submit"
                disabled={saving}
                className="bg-red-700 text-white text-sm font-medium px-5 py-2.5 rounded-lg hover:bg-red-800 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Đang lưu…' : mode === 'new' ? 'Tạo tài nguyên' : 'Lưu thay đổi'}
              </button>
            </div>
          </form>
        </FormShell>
      </div>

      {previewing && (
        <PreviewModal onClose={() => setPreviewing(false)}>
          {(ctx) => <ResourceDetail resource={toPreviewRow(state, id)} ctx={ctx} />}
        </PreviewModal>
      )}
    </AppShell>
  );
}

const inputClass =
  'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500';

interface SectionProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
}
function Section({ title, subtitle, children }: SectionProps) {
  return (
    <section className="brand-card p-6 space-y-5">
      <div>
        <h2 className="font-semibold">{title}</h2>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

interface FieldProps {
  label: string;
  required?: boolean;
  children: ReactNode;
}
function Field({ label, required, children }: FieldProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {children}
    </div>
  );
}

interface CheckboxGroupProps<T extends string> {
  label: string;
  options: ReadonlyArray<{ value: T; label: string }>;
  values: T[];
  onToggle: (value: T) => void;
}
function CheckboxGroup<T extends string>({ label, options, values, onToggle }: CheckboxGroupProps<T>) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {options.map((opt) => (
          <label key={opt.value} className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={values.includes(opt.value)}
              onChange={() => onToggle(opt.value)}
            />
            {opt.label}
          </label>
        ))}
      </div>
    </div>
  );
}
